import { produce } from "immer";
import {
  GET_ROTA_STATS,
  GET_ROTA_STATS_FAILURE,
  GET_ROTA_STATS_SUCCESS,
  GET_SURVEY_STATS,
  GET_SURVEY_STATS_FAILURE,
  GET_SURVEY_STATS_SUCCESS,
  GET_USER_STATS,
  GET_USER_STATS_FAILURE,
  GET_USER_STATS_SUCCESS,
  SET_DROP_REQUESTS,
  SET_OPEN_SHIFT,
  SET_MY_DROPPED_SHIFTS,
  SET_UPCOMING_SHIFTS,
  SET_PULSE_SURVEY_LIST,
  REMOVE_PULSE_SURVEY,
  SET_FEED_LIST,
  SET_SWAP_REQUESTS,
  SET_SWAP_APPROVALS,
  TOGGLE_VIEW_MODAL,
  // modern dashboard
  GET_HR_STATS,
  GET_HR_STATS_FAILURE,
  GET_HR_STATS_SUCCESS,
  GET_SHIFT_APPROVALS,
  GET_SHIFT_APPROVALS_FAILURE,
  GET_SHIFT_APPROVALS_SUCCESS,
  GET_STAFF_OPS,
  GET_STAFF_OPS_FAILURE,
  GET_STAFF_OPS_SUCCESS,
  GET_TEAM_ACTIVITY,
  GET_TEAM_ACTIVITY_FAILURE,
  GET_TEAM_ACTIVITY_SUCCESS,
  GET_LEAD_ANALYTICS,
  GET_LEAD_ANALYTICS_FAILURE,
  GET_LEAD_ANALYTICS_SUCCESS,
  GET_TEAM_COMM,
  GET_TEAM_COMM_FAILURE,
  GET_TEAM_COMM_SUCCESS,
  GET_MAINTENANCE_STATS,
  GET_MAINTENANCE_STATS_FAILURE,
  GET_MAINTENANCE_STATS_SUCCESS,
  GET_DISCIPLINARY_STATS,
  GET_DISCIPLINARY_STATS_FAILURE,
  GET_DISCIPLINARY_STATS_SUCCESS,
} from "./constants";

const userStatsInitialState = {
  birthday_list: {
    today: [],
    tomorrow: [],
  },
  employee_upcoming_leaves_reqeusts: [],
  new_joined_employees: [],
  recent_feeds: [],
  expiringDocs: {
    display_docs: [],
    all_docs: [],
  },
};

const rotaStatsInitialState = {
  shifts: [],
  open_shifts: [],
};

const expiringDocsInitialState = {
  display_docs: [],
  all_docs: [],
};

const hrComplianceInitialState = {
  hr_overview: {
    total_employees: 0,
    expired_docs: 0,
    expiring_soon: 0,
    leaving_soon: 0,
  },
  expired_documents: [],
  expiring_soon: [],
  upcoming_leavers: [],
};

const surveyStatsInitialState = {
  active: [],
  upcoming: [],
};

const shiftApprovalsInitialState = {
  available: [],
  claimed: [],
  drop_requests: [],
  swap_requests: [],
};

const staffOpsInitialState = {
  annual_leave: 0,
  sick_leave: 0,
  pending_my_approval: 0,
  late_clock_in: [],
  missed_clock_in: [],
  people_on_leave_today: [],
  swap_requests: [],
  upcoming_leaves: [],
};

const teamActivityInitialState = {
  birthdays_today: [],
  new_joiners: [],
};

const leadAnalyticsInitialState = {
  month_to_date: [],
};

const teamCommInitialState = {
  feeds: [],
};

const disciplinaryStatsInitialState = {
  disciplinary_overview: {
    active_cases: 0,
    hearings_this_week: 0,
    awaiting_outcome: 0,
    open_appeals: 0,
    active_warnings: 0,
    warnings_expiring: 0,
    suspended_now: 0,
    unacknowledged_outcomes: 0,
  },
  open_cases: [],
  upcoming_hearings: [],
  warnings_expiring: [],
  pending_appeals: [],
};

const maintenanceStatsInitialState = {
  manager_requests: [],
  my_requests: [],
  schedule: [],
  overdue_schedule: [],
};

export const initialState = {
  isFetchingUserStats: false,
  userStats: userStatsInitialState,
  isFetchingRotaStats: false,
  rotaStats: rotaStatsInitialState,

  // Rota Data
  openShifts: [],
  dropRequests: [],
  myDroppedShifts: [],
  upcomingShifts: [],
  swapRequests: [],
  swapApprovals: [],
  birthdayList: [],

  // Survey Data
  pulseSurveyList: [],

  // Feed Data
  feedList: [],
  popularFeeds: [],
  kudosList: [],

  // HR
  hrStats: {
    total_onboarding: 0,
    completed: 0,
    action_required: 0,
    overall_progress: 0,
  },

  // Leaves
  otherLeaves: [],
  annualLeaves: [],

  // Expiring Docs Data
  expiringDocs: expiringDocsInitialState,

  // staff
  staffBudget: {},

  // Current Week Leaves
  currentWeekLeaves: [],

  // View Modal
  viewModal: {
    open: false,
  },

  // HR Stats
  isFetchingHrStats: false,
  hrCompliance: hrComplianceInitialState,

  // Shift Approvals
  isFetchingShiftApprovals: false,
  shiftApprovals: shiftApprovalsInitialState,

  // Survey Stats
  isFetchingSurveyStats: false,
  surveyStats: surveyStatsInitialState,

  // Shift Ops
  isFetchingStaffOps: false,
  staffOps: staffOpsInitialState,

  // Team Activity
  isFetchingTeamActivity: false,
  teamActivity: teamActivityInitialState,

  // Lead Analytics
  isFetchingLeadAnalytics: false,
  leadAnalytics: leadAnalyticsInitialState,

  // Team Communication
  isFetchingTeamComm: false,
  teamComm: teamCommInitialState,

  // Maintenance Stats
  isFetchingMaintenanceStats: false,
  maintenanceStats: maintenanceStatsInitialState,

  // Disciplinary Stats
  isFetchingDisciplinaryStats: false,
  disciplinaryStats: disciplinaryStatsInitialState,
};

const StatsReducer = (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      // List
      case GET_USER_STATS:
        draft.isFetchingUserStats = true;
        draft.expiringDocs = expiringDocsInitialState;
        draft.staffBudget = {};
        break;
      case GET_USER_STATS_SUCCESS:
        draft.isFetchingUserStats = false;
        draft.userStats = action.payload;
        draft.feedList = action.payload?.recent_feeds ?? [];
        draft.expiringDocs =
          action.payload?.expiringDocs ?? expiringDocsInitialState;
        draft.birthdayList = action.payload?.birthdayList ?? [];
        draft.staffBudget = action.payload?.staff_budget ?? {};
        draft.popularFeeds = action.payload?.most_popular_feeds ?? [];
        draft.hrStats = action.payload?.hr_stats ?? {};
        draft.kudosList = action.payload?.kudos_leaderboard ?? [];
        draft.otherLeaves = action.payload?.leave_requests?.other_leave ?? [];
        draft.annualLeaves = action.payload?.leave_requests?.annual_leave ?? [];
        draft.currentWeekLeaves =
          action.payload?.current_week_leave_requests ?? [];

        break;
      case GET_USER_STATS_FAILURE:
        draft.isFetchingUserStats = false;
        break;

      case GET_ROTA_STATS:
        draft.isFetchingRotaStats = true;
        break;
      case GET_ROTA_STATS_SUCCESS:
        const { open_shifts, drop_shifts, swap_shifts, upcomming_shifts } =
          action.payload;
        draft.isFetchingRotaStats = false;
        draft.rotaStats = action.payload;
        draft.openShifts = open_shifts ?? [];
        draft.myDroppedShifts = drop_shifts?.my_drop_shifts ?? [];
        draft.dropRequests = drop_shifts?.approval_drop_shifts ?? [];
        draft.upcomingShifts = upcomming_shifts ?? [];
        draft.swapRequests = swap_shifts?.swap_requests ?? [];
        draft.swapApprovals = swap_shifts?.swap_approval_required ?? [];
        break;
      case GET_ROTA_STATS_FAILURE:
        draft.isFetchingRotaStats = false;
        break;

      case SET_OPEN_SHIFT:
        draft.openShifts = action.payload;
        break;

      case SET_DROP_REQUESTS:
        draft.dropRequests = action.payload;
        break;

      case SET_MY_DROPPED_SHIFTS:
        draft.myDroppedShifts = action.payload;
        break;

      case SET_UPCOMING_SHIFTS:
        draft.upcomingShifts = action.payload;
        break;

      case SET_PULSE_SURVEY_LIST:
        draft.pulseSurveyList = action.payload;
        break;

      case REMOVE_PULSE_SURVEY:
        draft.pulseSurveyList = draft.pulseSurveyList.filter(
          (survey) => survey.id !== action.payload,
        );
        break;

      case SET_FEED_LIST:
        draft.feedList = action.payload;
        break;

      case SET_SWAP_REQUESTS:
        draft.swapRequests = action.payload;
        break;

      case SET_SWAP_APPROVALS:
        draft.swapApprovals = action.payload;
        break;

      case TOGGLE_VIEW_MODAL:
        draft.viewModal = action.payload;
        break;

      case GET_HR_STATS:
        draft.isFetchingHrStats = true;
        break;
      case GET_HR_STATS_SUCCESS:
        draft.isFetchingHrStats = false;
        draft.hrCompliance = action.payload;
        break;
      case GET_HR_STATS_FAILURE:
        draft.isFetchingHrStats = false;
        break;

      case GET_SURVEY_STATS:
        draft.isFetchingSurveyStats = true;
        break;
      case GET_SURVEY_STATS_SUCCESS:
        draft.isFetchingSurveyStats = false;
        draft.surveyStats = action.payload;
        break;
      case GET_SURVEY_STATS_FAILURE:
        draft.isFetchingSurveyStats = false;
        break;

      case GET_SHIFT_APPROVALS:
        draft.isFetchingShiftApprovals = true;
        break;
      case GET_SHIFT_APPROVALS_SUCCESS:
        draft.isFetchingShiftApprovals = false;
        draft.shiftApprovals = action.payload;
        break;
      case GET_SHIFT_APPROVALS_FAILURE:
        draft.isFetchingShiftApprovals = false;
        break;

      case GET_STAFF_OPS:
        draft.isFetchingStaffOps = true;
        break;
      case GET_STAFF_OPS_SUCCESS:
        draft.isFetchingStaffOps = false;
        draft.staffOps = action.payload;
        break;
      case GET_STAFF_OPS_FAILURE:
        draft.isFetchingStaffOps = false;
        break;

      case GET_TEAM_ACTIVITY:
        draft.isFetchingTeamActivity = true;
        break;
      case GET_TEAM_ACTIVITY_SUCCESS:
        draft.isFetchingTeamActivity = false;
        draft.teamActivity = action.payload;
        break;
      case GET_TEAM_ACTIVITY_FAILURE:
        draft.isFetchingTeamActivity = false;
        break;

      case GET_LEAD_ANALYTICS:
        draft.isFetchingLeadAnalytics = true;
        break;
      case GET_LEAD_ANALYTICS_SUCCESS:
        draft.isFetchingLeadAnalytics = false;
        draft.leadAnalytics = action.payload;
        break;
      case GET_LEAD_ANALYTICS_FAILURE:
        draft.isFetchingLeadAnalytics = false;
        break;

      case GET_TEAM_COMM:
        draft.isFetchingTeamComm = true;
        break;
      case GET_TEAM_COMM_SUCCESS:
        draft.isFetchingTeamComm = false;
        draft.teamComm = action.payload;
        break;
      case GET_TEAM_COMM_FAILURE:
        draft.isFetchingTeamComm = false;
        break;

      case GET_MAINTENANCE_STATS:
        draft.isFetchingMaintenanceStats = true;
        break;
      case GET_MAINTENANCE_STATS_SUCCESS:
        draft.isFetchingMaintenanceStats = false;
        draft.maintenanceStats = action.payload;
        break;
      case GET_MAINTENANCE_STATS_FAILURE:
        draft.isFetchingMaintenanceStats = false;
        break;

      case GET_DISCIPLINARY_STATS:
        draft.isFetchingDisciplinaryStats = true;
        break;
      case GET_DISCIPLINARY_STATS_SUCCESS:
        draft.isFetchingDisciplinaryStats = false;
        draft.disciplinaryStats = action.payload;
        break;
      case GET_DISCIPLINARY_STATS_FAILURE:
        draft.isFetchingDisciplinaryStats = false;
        break;

      default:
        return state;
    }
  });

export default StatsReducer;
